import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useApp } from '@/contexts/AppContext';
import { reportRuntimeEvent } from '@/lib/runtime-monitor';

function toMessage(reason: unknown) {
  if (reason instanceof Error) return reason.message;
  if (typeof reason === 'string') return reason;
  try {
    return JSON.stringify(reason);
  } catch {
    return 'Unknown rejection reason';
  }
}

export default function AppRuntimeMonitor() {
  const location = useLocation();
  const { user } = useApp();

  useEffect(() => {
    if (!user) return;

    const handleWindowError = (event: ErrorEvent) => {
      const message = event.error?.message || event.message || 'Unknown runtime error';
      void reportRuntimeEvent({
        userId: user.id,
        severity: 'error',
        source: 'window.error',
        message,
        path: location.pathname,
        metadata: {
          fileName: event.filename || null,
          line: event.lineno || null,
          column: event.colno || null,
        },
      });
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      void reportRuntimeEvent({
        userId: user.id,
        severity: 'error',
        source: 'window.unhandledrejection',
        message: toMessage(event.reason),
        path: location.pathname,
      });
    };

    window.addEventListener('error', handleWindowError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleWindowError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, [location.pathname, user]);

  return null;
}
